/**
 * ShirtWerk — global theme utilities.
 * Loaded once in layout/theme.liquid. Exposes small, dependency-free helpers
 * on window.ShirtWerk so component scripts can stay self-contained without a
 * build step / module bundler.
 */
window.ShirtWerk = (function () {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, initialFocus) {
    const focusable = Array.from(container.querySelectorAll(FOCUSABLE));
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    (initialFocus || first).focus();

    return function release() {
      container.removeEventListener('keydown', onKeydown);
    };
  }

  function debounce(fn, wait) {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  async function fetchSections(sectionIds) {
    const url = `${window.location.pathname}?sections=${sectionIds.join(',')}`;
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('sections-fetch-failed');
    return response.json();
  }

  function announce(regionId, message) {
    const region = document.getElementById(regionId);
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    return (format || '${{amount}}').replace('{{amount}}', value);
  }

  async function addToCart(formData) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [formData] }),
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.description || data.message || 'cart-add-failed');
      error.data = data;
      throw error;
    }
    return data;
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!response.ok) throw new Error('cart-fetch-failed');
    return response.json();
  }

  /**
   * Zentraler, seitenunabhängiger Add-to-Cart-Handler: läuft unabhängig davon,
   * ob ein Cart Drawer im DOM existiert (settings.cart_type kann "page" sein),
   * damit Quick-Add auf Produktkarten überall funktioniert. Komponenten, die
   * zusätzliches Verhalten nach dem Hinzufügen brauchen (z. B. der Drawer, der
   * sich öffnen soll), hören auf "cart:added".
   */
  document.addEventListener('cart:add', async (e) => {
    try {
      await addToCart(e.detail.formData);
      const cart = await fetchCart();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: cart.item_count } }));
      document.dispatchEvent(new CustomEvent('cart:added', { detail: e.detail }));
    } catch (err) {
      document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: err.data && err.data.description } }));
    }
  });

  return { trapFocus, debounce, fetchSections, announce, formatMoney, addToCart, fetchCart };
})();
